import loadOnScroll from "../utility/loadOnScroll"
import { bindDialogs } from './dialog'
import WebGL from "three/examples/jsm/capabilities/WebGL"
import {
	OrthographicCamera,
	PlaneGeometry,
	Points,
	Scene,
	ShaderMaterial,
	TextureLoader,
	WebGLRenderer,
	LinearFilter
} from "three"

const dotSpacing = 11
const dotSize = 8
const dotEdge = 5
const dotColour = "#B9B9B9"
const rowRatio = 2 / Math.sqrt(3)
const fallbackClass = "country-map--silhouette"

const loadInSweepMs = 350
const loadInGrowMs = 500
const loadInMs = loadInSweepMs + loadInGrowMs

// land/sea cut-off on the texture mask
const maskThreshold = 0.1

// both as fractions of the 0 to 1 the shader runs on
const loadInSpread = loadInSweepMs / loadInMs
const loadInWindow = loadInGrowMs / loadInMs

function createScene(canvas, container, textureUrl, colour) {

	// show map silhouette
	if (!WebGL.isWebGL2Available()) {
		container.classList.add(fallbackClass)
		return null
	}

	const renderer = new WebGLRenderer({ canvas, alpha: true, antialias: false })
	renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))

	const scene = new Scene()

	// frustum is the unit square, so the plane fills the canvas at any aspect
	const camera = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10)
	camera.position.z = 1

	new TextureLoader().load(textureUrl, mapTex => {
		mapTex.generateMipmaps = true
		mapTex.minFilter = LinearFilter
		mapTex.magFilter = LinearFilter
		mapTex.needsUpdate = true

		const dottedMap = createDottedMap(scene, mapTex, colour)
		onResize(renderer, container, camera, scene, dottedMap)
		observeSize(renderer, container, camera, scene, dottedMap)
		whenScrolledIn(container, () => animateLoadIn(renderer, camera, scene, dottedMap))
	}, undefined, () => {
		// texture failed, the svg silhouette stands in for the dots
		container.classList.add(fallbackClass)
	})

	return { canvas, renderer, scene, camera }
}

const glslFloat = n => n.toFixed(4)

function createDottedMap(scene, mapTex, colour) {

	const dotMaterial = new ShaderMaterial({
		vertexShader: `
			uniform sampler2D u_map_tex;
			uniform float u_dot_size;
			uniform vec2 u_grid;
			uniform float u_load_in;
			varying float vMask;

			void main() {
				// shift cols by half a cell
				float col = floor(uv.x * u_grid.x + 0.5);
				float shift = -mod(col, 2.0) * 0.5 / u_grid.y;

				// shifted texel
				vec2 st = uv + vec2(0.0, shift);
				vec2 inMap = step(vec2(0.0), st) * step(st, vec2(1.0));
				vMask = texture2D(u_map_tex, st).a * min(inMap.x, inMap.y);

				// 0 to 1 across the map, so dots fill in from the middle out
				float centre = distance(position.xy + vec2(0.0, shift), vec2(0.0)) * 1.4142136;
				float turn = clamp(
					(u_load_in - centre * ${glslFloat(loadInSpread)})
					/ ${glslFloat(loadInWindow)},
					0.0, 1.0);

				float grow = smoothstep(0.0, 1.0, turn);

				// scaling the sprite scales the feather too, and a size of zero
				// is not rasterised at all, so dots start invisible
				gl_PointSize = step(${glslFloat(maskThreshold)}, vMask) * u_dot_size
					* grow;
				gl_Position = projectionMatrix * modelViewMatrix *
					vec4(position + vec3(0.0, shift, 0.0), 1.0);
			}
		`,
		fragmentShader: `
			uniform float u_dot_edge;
			uniform float u_fill;
			uniform vec3 u_colour;
			varying float vMask;
			void main() {
				float r = length(gl_PointCoord.xy - vec2(0.5)) * 2.0 / u_fill;
				float a = 1. - smoothstep(1. - u_dot_edge, 1., r);
				gl_FragColor = vec4(u_colour, a * step(${glslFloat(maskThreshold)}, vMask));
			}
		`,
		uniforms: {
			u_map_tex: { type: "t", value: mapTex },
			u_dot_size: { type: "f", value: 0 },
			u_dot_edge: { type: "f", value: 0 },
			u_fill: { type: "f", value: 1 },
			u_colour: { type: "v3", value: parseColour(colour) || parseColour(dotColour) },
			u_grid: { type: "v2", value: [1, 1] },
			u_load_in: { type: "f", value: 0 },
		},
		alphaTest: false,
		transparent: true,
		depthWrite: false
	})

	const points = new Points(new PlaneGeometry(1, 1, 1, 1), dotMaterial)
	points.frustumCulled = false
	scene.add(points)

	return {
		points,
		dotMaterial,
		grid: [0, 0]
	}
}


function onResize(renderer, container, camera, scene, dottedMap) {

	const width = Math.floor(container.clientWidth)
	const height = Math.floor(container.clientHeight)
	if (!width || !height) return

	renderer.setSize(width, height)
	const segmentsX = Math.max(2, Math.round(width / dotSpacing))
	const column = width / segmentsX
	const segmentsY = Math.max(2, Math.round(height / (column * rowRatio)))

	const [prevX, prevY] = dottedMap.grid

	if (segmentsX !== prevX || segmentsY !== prevY) {
		dottedMap.grid = [segmentsX, segmentsY]
		dottedMap.points.geometry.dispose()
		dottedMap.points.geometry = new PlaneGeometry(1, 1, segmentsX, segmentsY)
		dottedMap.dotMaterial.uniforms.u_grid.value = [segmentsX, segmentsY]
	}

	const pixelRatio = renderer.getPixelRatio()
	const dotPx = dotSize * pixelRatio
	const edge = Math.min(dotEdge, dotPx / 2)
	const sprite = dotPx + edge * 2

	dottedMap.dotMaterial.uniforms.u_dot_size.value = sprite
	dottedMap.dotMaterial.uniforms.u_fill.value = dotPx / sprite
	dottedMap.dotMaterial.uniforms.u_dot_edge.value = (edge * 2) / dotPx

	renderer.render(scene, camera)
}


function whenScrolledIn(el, callback) {
	let started = false
	const start = () => {
		if (started) return
		started = true
		callback()
	}
	el.addEventListener('animations:start', start, { once: true })
	loadOnScroll(el, start, -200)
}

function animateLoadIn(renderer, camera, scene, dottedMap) {
	const loadIn = dottedMap.dotMaterial.uniforms.u_load_in
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		loadIn.value = 1
		renderer.render(scene, camera)
		return
	}
	const start = performance.now()
	const step = now => {
		const t = Math.min(1, (now - start) / loadInMs)
		// linear on purpose. this gates when each dot's turn starts, so easing
		// here would collapse the stagger. the ease is per dot, in the shader
		loadIn.value = t
		renderer.render(scene, camera)
		if (t < 1) requestAnimationFrame(step)
	}
	requestAnimationFrame(step)
}


function observeSize(renderer, container, camera, scene, dottedMap) {
	let queued = false
	const resize = () => {
		if (queued) return
		queued = true
		requestAnimationFrame(() => {
			queued = false
			onResize(renderer, container, camera, scene, dottedMap)
		})
	}
    window.addEventListener("resize", resize)
}





function parseColour(value) {
    const hex = String(value || "").replace("#", "")
    if (!/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return null
    return (hex.length === 3 ? hex.replace(/./g, "$&$&") : hex)
        .match(/../g).map(c => parseInt(c, 16) / 255)
}


// init

window.addEventListener('elementor/frontend/init', () => {
	elementorFrontend.hooks.addAction('frontend/element_ready/country-map.default', $scope => {
		const container = $scope[0] || $scope
        const mod = container.querySelector('.country-map-module')
		const el = container.querySelector('.js-country-map')
		if (!el) return

		const data = JSON.parse(el.querySelector('.js-country-map__script')?.textContent || '{}')
		const mapTexture = data.texture
		if (!mapTexture) return

		const canvas = el.querySelector('.country-map__canvas')
		loadOnScroll(el, () => createScene(canvas, el, mapTexture, data.colour), 200)
		bindDialogs(mod)
	})
})
